import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { useToast } from 'hooks/use-toast';
import {
  useDisconnectComposioConnection,
  useGetComposioConnections,
  useGetComposioToolkits,
  useInitiateComposioConnection,
} from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import type {
  IComposioConnectionEntry,
  IToolDetails,
} from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { getComposioConfig } from './tool-detail-utils';

const PENDING_TIMEOUT_MS = 2 * 60 * 1000;

type ComposioOauthMessage = {
  type?: string;
  ok?: boolean;
  toolId?: string;
  toolkit?: string;
  error?: string;
};

function statusBadge(
  entry: IComposioConnectionEntry | undefined,
  t: (key: string, options?: never) => string
) {
  const status = entry?.status ?? 'NONE';
  if (status === 'ACTIVE') {
    return (
      <span className="flex items-center gap-1.5 text-sm font-medium text-[#079455]">
        <span className="h-2 w-2 rounded-full bg-[#079455]" />
        {t('CONNECTED') || 'Connected'}
      </span>
    );
  }
  if (status === 'INITIATED' || status === 'INITIALIZING') {
    return <Badge variant="secondary">{t('CONNECTION_PENDING') || 'Pending'}</Badge>;
  }
  if (status === 'EXPIRED' || status === 'FAILED') {
    return <Badge variant="destructive">{status === 'EXPIRED' ? t('EXPIRED') || 'Expired' : t('FAILED') || 'Failed'}</Badge>;
  }
  return <Badge variant="outline">{t('NOT_CONNECTED') || 'Not connected'}</Badge>;
}

export function ComposioConnections({ tool }: { tool: IToolDetails }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { toolkits } = getComposioConfig(tool);

  const [pendingToolkit, setPendingToolkit] = useState<string | null>(null);
  const pendingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const connectionsQuery = useGetComposioConnections(tool.id, {
    refetchOnWindowFocus: true,
    refetchInterval: pendingToolkit ? 4000 : false,
  });
  const toolkitsQuery = useGetComposioToolkits(tool.id);
  const initiateMutation = useInitiateComposioConnection();
  const disconnectMutation = useDisconnectComposioConnection();

  const connections = useMemo(
    () => connectionsQuery.data?.connections ?? {},
    [connectionsQuery.data]
  );
  const availability = useMemo(() => {
    const map = new Map<
      string,
      { authConfigAvailable: boolean; reason?: string; authConfigId?: string }
    >();
    for (const entry of toolkitsQuery.data?.toolkits ?? []) {
      map.set(entry.toolkit, {
        authConfigAvailable: entry.authConfigAvailable,
        reason: entry.reason,
        authConfigId: entry.authConfigId,
      });
    }
    return map;
  }, [toolkitsQuery.data]);

  const clearPending = () => {
    if (pendingTimeoutRef.current) {
      clearTimeout(pendingTimeoutRef.current);
      pendingTimeoutRef.current = null;
    }
    setPendingToolkit(null);
  };

  // The backend callback page posts {type:'composio-oauth', ok, toolId, toolkit}
  // to the opener. It is served from the BACKEND origin (and may never arrive at
  // all when the hosted success page is used), so we validate by message shape
  // and keep polling + focus-refetch as the fallback.
  useEffect(() => {
    const handleMessage = (event: MessageEvent<ComposioOauthMessage>) => {
      const data = event.data;
      if (data?.type !== 'composio-oauth') {
        return;
      }
      // Prefer toolId match when present; otherwise accept any composio-oauth for this opener.
      if (data.toolId && data.toolId !== tool.id) {
        return;
      }
      clearPending();
      queryClient.invalidateQueries({ queryKey: ['tools', 'composio-connections', tool.id] });
      if (data.ok) {
        toast({
          variant: 'success',
          title: t('CONNECTED') || 'Connected',
          description: `${data.toolkit ?? ''} ${t('ACCOUNT_CONNECTED_SUCCESSFULLY') || 'account connected successfully.'}`.trim(),
        });
      } else {
        toast({
          variant: 'destructive',
          title: t('CONNECTION_FAILED') || 'Connection failed',
          description:
            data.error || t('TRY_CONNECTING_AGAIN') || 'The authorization did not complete. Please try again.',
        });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool.id]);

  useEffect(() => () => clearPending(), []);

  // Stop polling once the pending toolkit reaches a terminal state.
  useEffect(() => {
    if (!pendingToolkit) return;
    const status = connections[pendingToolkit]?.status;
    if (status === 'ACTIVE' || status === 'FAILED' || status === 'EXPIRED') {
      clearPending();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connections, pendingToolkit]);

  const handleConnect = async (toolkit: string) => {
    try {
      const response = await initiateMutation.mutateAsync({ toolId: tool.id, toolkit });
      if (!response?.redirectUrl) {
        throw new Error(t('NO_REDIRECT_URL') || 'No authorization URL returned.');
      }
      window.open(response.redirectUrl, '_blank', 'width=600,height=700');
      setPendingToolkit(toolkit);
      if (pendingTimeoutRef.current) clearTimeout(pendingTimeoutRef.current);
      pendingTimeoutRef.current = setTimeout(() => setPendingToolkit(null), PENDING_TIMEOUT_MS);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('CONNECTION_FAILED') || 'Connection failed',
        description:
          error instanceof Error
            ? error.message
            : t('FAILED_TO_START_AUTHORIZATION') || 'Failed to start the authorization flow.',
      });
    }
  };

  const handleDisconnect = async (toolkit: string) => {
    try {
      const response = await disconnectMutation.mutateAsync({ toolId: tool.id, toolkit });
      toast({
        variant: 'success',
        title: t('DISCONNECTED') || 'Disconnected',
        description: t('COMPOSIO_ACCOUNTS_REMOVED', {
          defaultValue: '{{count}} connected account(s) removed.',
          count: response?.removed ?? 0,
        }),
      });
      await queryClient.invalidateQueries({ queryKey: ['tools', 'composio-connections', tool.id] });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('DISCONNECT_FAILED') || 'Disconnect failed',
        description:
          error instanceof Error
            ? error.message
            : t('FAILED_TO_DISCONNECT') || 'Failed to disconnect the account.',
      });
    }
  };

  if (toolkits.length === 0) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-sm text-muted-foreground">
            {t('NO_TOOLKITS_CONFIGURED') ||
              'No toolkits configured yet. Edit this tool to add toolkits like OUTLOOK or MICROSOFT_TEAMS.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t('CONNECTOR_CONNECTIONS_HINT') ||
          'Connect your own accounts below. Each user connects separately — the agent acts on your behalf using your connections.'}
      </p>

      {connectionsQuery.isLoading ? (
        <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('LOADING_CONNECTIONS') || 'Loading connections...'}
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {toolkits.map((toolkit) => {
            const entry = connections[toolkit];
            const isConnected = entry?.connected === true;
            const toolkitAvailability = availability.get(toolkit);
            const authConfigMissing = toolkitAvailability?.authConfigAvailable === false;
            const isPendingThis = pendingToolkit === toolkit;
            const busy =
              (initiateMutation.isPending || disconnectMutation.isPending) &&
              (initiateMutation.variables?.toolkit === toolkit ||
                disconnectMutation.variables?.toolkit === toolkit);

            return (
              <Card key={toolkit}>
                <CardContent className="flex flex-col gap-3 p-4">
                  <div className="flex items-center justify-between gap-2">
                    <span className="break-all text-sm font-semibold">{toolkit}</span>
                    {statusBadge(entry, t as never)}
                  </div>
                  {toolkitAvailability?.authConfigId ? (
                    <p className="break-all font-mono text-xs text-muted-foreground">
                      {(t('AUTH_CONFIG') || 'auth config') + ': '}
                      {toolkitAvailability.authConfigId}
                    </p>
                  ) : toolkitAvailability?.reason && !authConfigMissing ? (
                    <p className="text-xs text-muted-foreground">{toolkitAvailability.reason}</p>
                  ) : null}
                  {isPendingThis && (
                    <p className="text-xs text-muted-foreground">
                      {t('COMPLETE_AUTH_IN_POPUP') ||
                        'Complete the sign-in in the popup window. This page updates automatically.'}
                    </p>
                  )}
                  <div className="flex justify-end">
                    {isConnected ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        disabled={busy}
                        onClick={() => handleDisconnect(toolkit)}
                      >
                        {busy ? (
                          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                        ) : (
                          t('DISCONNECT') || 'Disconnect'
                        )}
                      </Button>
                    ) : authConfigMissing ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              <Button size="sm" disabled>
                                {t('CONNECT') || 'Connect'}
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-64 text-sm leading-5">
                              {toolkitAvailability?.reason ||
                                t('AUTH_CONFIG_UNAVAILABLE') ||
                                'No auth configuration is available for this toolkit yet. Contact your administrator.'}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <Button
                        size="sm"
                        className="min-w-20"
                        disabled={busy || isPendingThis}
                        onClick={() => handleConnect(toolkit)}
                        aria-label={
                          busy || isPendingThis
                            ? t('CONNECTING') || 'Connecting'
                            : t('CONNECT') || 'Connect'
                        }
                      >
                        {busy || isPendingThis ? (
                          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                        ) : (
                          t('CONNECT') || 'Connect'
                        )}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
